import api from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useMutation } from '@mint-lib/query-client';

import { queryClient } from '../../queries/client.js';
import { changeApprovalProcessSchema } from '../../schemas.js';
import type { ApprovalObject } from '../../types.js';
import {
  CHANGE_APPROVAL_FLOW_STATUS_KEY,
  DELETE_APPROVAL_PROCESS_KEY,
  GET_APPROVAL_FLOW_STATUS_KEY,
  GET_APPROVAL_PROCESS_DETAILS_KEY,
  SEND_FOR_APPROVAL_FLOW_KEY,
  SEND_REMINDER_EMAIL_KEY,
} from '../constants/apiKeys.js';
import {
  CHANGE_APPROVAL_FLOW_STATUS,
  DELETE_APPROVAL_PROCESS,
  SEND_FOR_APPROVAL_FLOW,
  SEND_REMINDER_EMAIL,
} from '../constants/endpoints.js';

const sendForApprovalFlow = async (innerObj: ApprovalObject) => {
  const response = await api.post(SEND_FOR_APPROVAL_FLOW, innerObj);
  return changeApprovalProcessSchema.parse(response.data.data.items[0]);
};
const changeApprovalFlow = async (payload: {
  approvalProcessId?: string | null;
  status: 'progress' | 'approved' | 'rejected';
  comment: string;
}) => {
  const response = await api.put(CHANGE_APPROVAL_FLOW_STATUS, payload);
  return changeApprovalProcessSchema.parse(response.data.data.items[0]);
};
const deleteApprovalProcess = async (processId: string) => {
  const response = await api.delete(`${DELETE_APPROVAL_PROCESS}/${processId}/`);
  return response.data;
};
const sendReminderEmail = async (payload: {
  approvalProcessId?: string | null;
  approverId: string;
}) => {
  const response = await api.post(SEND_REMINDER_EMAIL, null, {
    params: {
      approval_process_id: payload.approvalProcessId,
      approver_id: payload.approverId,
    },
  });
  return response.data;
};

export const useSendApprovalFlow = () => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit');

  return useMutation({
    mutationKey: [SEND_FOR_APPROVAL_FLOW_KEY],
    mutationFn: (innerObj: ApprovalObject) => sendForApprovalFlow(innerObj),
    onSuccess: () => {
      enqueueSnackbar({
        title: t('Your buying plan has been sent for approval.'),
        variant: 'success',
        removable: true,
      });
    },
  });
};

export const useChangeApprovalFlowStatus = () => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit');

  return useMutation({
    mutationKey: [CHANGE_APPROVAL_FLOW_STATUS_KEY],
    mutationFn: changeApprovalFlow,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [GET_APPROVAL_FLOW_STATUS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_APPROVAL_PROCESS_DETAILS_KEY],
      });
      enqueueSnackbar({
        title:
          variables.status === 'approved'
            ? t('This buying plan has been approved.')
            : t('This buying plan has been rejected.'),
        variant: 'success',
        removable: true,
      });
    },
  });
};

export const useDeleteApprovalProcess = () => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit');

  return useMutation({
    mutationKey: [DELETE_APPROVAL_PROCESS_KEY],
    mutationFn: (processId: string) => deleteApprovalProcess(processId),
    onSuccess: () => {
      enqueueSnackbar({
        title: t('Your approval request has been successfully withdrawn.'),
        variant: 'success',
        removable: true,
      });
    },
  });
};

export const useSendReminderEmail = () => {
  return useMutation({
    mutationKey: [SEND_REMINDER_EMAIL_KEY],
    mutationFn: sendReminderEmail,
  });
};
